import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import TrafficList from "./TrafficList";
import TrafficBar from "./TrafficBar";
import MemoTrafficMap from "./trafficMonitoringMap/TrafficMonitoringMap";

import useAreaData from "../data/useAreaData";
import useTrafficMonitoringGeom from "../data/useTrafficMonitoringGeom";
import useAreaGeom from "../data/useAreaGeom";
import useEventsMonioringData from "../data/useEventsMonioringData";
import { fetchActiveEvents } from "../data/fetchActiveEvents";

export default function AreaView() {
  const { t } = useTranslation();
  const { data: areas, loading: loadingAreas } = useAreaData();
  const { data: netData, loading: loadingNetwork } = useTrafficMonitoringGeom();
  const { data: polyData, loading: loadingPolygons } = useAreaGeom();
  const {
    events: globalEvents,
    loading: loadingGlobEvs,
  } = useEventsMonioringData();

  const [selectedArea, setSelectedArea] = useState(null);
  const [networkSource, setNetworkSource] = useState(null);
  const [areaRectSource, setAreaRectSource] = useState(null);
  const [onlyActive, setOnlyActive] = useState([]);
  const [onlyJam, setOnlyJam] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const didLoadNetwork = useRef(false);
  const didLoadEvents = useRef(false);

  // 1️⃣ default to “All Areas” or first
  useEffect(() => {
    if (!Array.isArray(areas) || areas.length === 0) return;

    // 1a) if nothing selected yet, default to “All Areas” (100) or first
    if (!selectedArea) {
      const allA = areas.find((a) => a.area_id === 100);
      setSelectedArea(allA || areas[0]);
      return;
    }

    // 1b) otherwise look up the _new_ object by ID
    const fresh = areas.find((a) => a.area_id === selectedArea.area_id);
    if (fresh) {
      // re-assign to the fresh reference so TrafficBar sees it in its data array
      setSelectedArea(fresh);
    } else {
      // fallback if that ID really vanished
      const allA = areas.find((a) => a.area_id === 100);
      setSelectedArea(allA || areas[0]);
    }
  }, [areas]);

  // 2️⃣ build networkSource (guard null)
  useEffect(() => {
    if (!selectedArea || !netData?.features) return;
    if (selectedArea.area_id === 100) {
      setNetworkSource(netData);
    } else {
      setNetworkSource({
        type: "FeatureCollection",
        features: netData.features.filter(
          (f) => f.properties.area_id === selectedArea.area_id
        ),
      });
    }
  }, [netData, selectedArea]);

  // 3️⃣ build areaRectSource (guard null)
  useEffect(() => {
    if (!selectedArea || !polyData?.features) return;
    if (selectedArea.area_id === 100) {
      setAreaRectSource(polyData);
    } else {
      setAreaRectSource({
        type: "FeatureCollection",
        features: polyData.features.filter(
          (f) => f.properties.area_id === selectedArea.area_id
        ),
      });
    }
  }, [polyData, selectedArea]);

  // 4️⃣ fetch & poll events
  useEffect(() => {
    // for “all areas” just use globalEvents
    if (selectedArea?.area_id === 100) {
      setOnlyActive(globalEvents);
      setOnlyJam(globalEvents.filter((e) => e.category?.id === 10));
      setLoadingEvents(false);
      return;
    }
    // otherwise guard
    if (!areaRectSource?.features?.length) {
      setOnlyActive([]);
      setOnlyJam([]);
      setLoadingEvents(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingEvents(true);
      const feat = areaRectSource.features[0];
      const coords = feat.geometry.coordinates[0];
      const simple = {
        ...feat,
        geometry: { ...feat.geometry, coordinates: coords },
      };
      try {
        const ev = await fetchActiveEvents(simple, "/events/area");
        if (!cancelled) {
          setOnlyActive(ev);
          setOnlyJam(ev.filter((e) => e.category?.id === 10));
        }
      } catch {
        if (!cancelled) {
          setOnlyActive([]);
          setOnlyJam([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingEvents(false);
          didLoadEvents.current = true;
        }
      }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [selectedArea, areaRectSource, globalEvents]);

  // ─── 4) Memos (all must run before any return) ───
  const areaMetrics = useMemo(() => selectedArea?.metrics || [], [
    selectedArea,
  ]);

  // 7️⃣ render
  const statsEvents = [
    {
      name: t("trafficMonitoring.monitoring.metrics.numberOfEvents.name"),
      values: [{ label: t("trafficMonitoring.monitoring.metrics.labels.total"), value: onlyActive.length, status: "neutral" }],
      description: t("trafficMonitoring.monitoring.metrics.numberOfEvents.descriptionArea"),
    },
  ];
  const statsJams = [
    {
      name: t("trafficMonitoring.monitoring.metrics.numberOfJams.name"),
      values: [{ label: t("trafficMonitoring.monitoring.metrics.labels.total"), value: onlyJam.length, status: "neutral" }],
      description: t("trafficMonitoring.monitoring.metrics.numberOfJams.descriptionArea"),
    },
  ];

  const allMetrics = useMemo(
    () => [...areaMetrics, ...statsEvents, ...statsJams],
    [areaMetrics, statsEvents, statsJams]
  );

  // 5️⃣ first spinner until areas+geom
  if (
    (loadingAreas || loadingNetwork || loadingPolygons) &&
    !didLoadNetwork.current
  ) {
    return <FullPageSpinner />;
  }
  didLoadNetwork.current = true;

  // 6️⃣ then spinner until events loaded once
  if (loadingEvents && !didLoadEvents.current) {
    return <FullPageSpinner />;
  }

  return (
    <Grid
      container
      spacing={2}
      sx={{
        height: "100%",
        minHeight: "80vh",
      }}
    >
      {/* Sidebar stats */}
      <Grid item xs={12} md={3}>
        <MDBox
          sx={{
            height: { xs: "auto", md: "100%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              py: 1,
            }}
          >
            <TrafficList data={allMetrics} />
          </MDBox>
        </MDBox>
      </Grid>

      {/* Map + selector */}
      <Grid item xs={12} md={9}>
        <MDBox
          sx={{
           height: { xs: "420px", md: "100%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TrafficBar
            data={areas}
            selectedData={selectedArea}
            setSelectedData={setSelectedArea}
          />
          <MDBox
            sx={{
              flexGrow: 1,
                minHeight: { xs: "320px", md: 0 },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <MemoTrafficMap
              onlyActive={onlyActive}
              networkSource={networkSource}
              areaRectSource={areaRectSource}
            />
          </MDBox>
        </MDBox>
      </Grid>
    </Grid>
  );
}

function FullPageSpinner() {
  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{ height: "100%" }}
    >
      <CircularProgress />
    </Grid>
  );
}
